import { ampli, WebsiteCtaClickedProperties } from 'src/ampli'

const UNKNOWN_VALUE = 'Unknown'

export interface CtaAnalyticsContext {
  module?: string
  heading?: string
}

interface TrackWebsiteCtaClickedParams extends CtaAnalyticsContext {
  ctaText?: unknown
  pagePath?: unknown
  targetUrl?: unknown
}

const cleanText = (value?: unknown) => {
  if (value === null || value === undefined) return undefined
  return String(value).replace(/\s+/g, ' ').trim() || undefined
}

export const getPortableTextBlockText = (block: any) =>
  cleanText(
    block?.children
      ?.map((child: any) => child?.text)
      .filter(Boolean)
      .join(' ')
  )

export const getFirstPortableTextBlockText = (
  blocks: any[] = [],
  fallback = UNKNOWN_VALUE
) => {
  const portableTextBlocks = Array.isArray(blocks) ? blocks : []
  const textBlock = portableTextBlocks.find(
    block => block?._type === 'block' && getPortableTextBlockText(block)
  )

  return getPortableTextBlockText(textBlock) || fallback
}

export const getPortableTextHeading = (
  blocks: any[] = [],
  fallback = UNKNOWN_VALUE
) => {
  const portableTextBlocks = Array.isArray(blocks) ? blocks : []
  const headingBlock = portableTextBlocks.find(
    block =>
      (block?._type === 'sectionHeader' && cleanText(block?.title)) ||
      (block?._type === 'block' &&
        ['h1', 'h2', 'h3'].includes(block?.style) &&
        getPortableTextBlockText(block))
  )

  if (headingBlock?._type === 'sectionHeader') {
    return cleanText(headingBlock.title) || fallback
  }

  return getPortableTextBlockText(headingBlock) || fallback
}

export const trackWebsiteCtaClicked = ({
  ctaText,
  heading,
  module,
  pagePath,
  targetUrl,
}: TrackWebsiteCtaClickedParams) => {
  if (typeof window === 'undefined') return

  try {
    const properties: WebsiteCtaClickedProperties = {
      ctaText: cleanText(ctaText) || UNKNOWN_VALUE,
      heading: cleanText(heading) || UNKNOWN_VALUE,
      module: cleanText(module) || UNKNOWN_VALUE,
      pagePath: cleanText(pagePath) || window.location.pathname,
    }
    const cleanedTargetUrl = cleanText(targetUrl)

    if (cleanedTargetUrl) {
      properties.targetUrl = cleanedTargetUrl
    }

    ampli.websiteCtaClicked(properties)

    // Flush immediately only when navigating away from the current domain.
    // For internal Gatsby navigation (starts with '/' or '#'), the page does not
    // unload so Amplitude's built-in batching is sufficient.
    // For external destinations (e.g. member.asktia.com) the browser unloads the
    // page before the batch fires, which would silently drop the event.
    const isExternalNavigation =
      cleanedTargetUrl &&
      !cleanedTargetUrl.startsWith('/') &&
      !cleanedTargetUrl.startsWith('#')

    if (isExternalNavigation) {
      ampli.flush().promise.catch(flushReason => {
        console.error('Unable to flush Website CTA Clicked', flushReason)
      })
    }
  } catch (reason) {
    console.error('Unable to track Website CTA Clicked', reason)
  }
}
